import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchPricingCustomers,
  searchPricingParts,
  getCustomerPricingContext,
  resolveCustomerPrice,
} from '../../services/pricingApi';
import { PageHeader, Button } from '../ui';

const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
};

const DEBOUNCE_MS = 300;

/**
 * Generic debounced typeahead.
 * search(q) -> Promise<array>; renderOption(item) -> label node;
 * onPick(item) commits a selection; display(item) -> input text after pick.
 */
export const TypeAhead = ({ placeholder, search, renderOption, display, onPick, disabled, pickedLabel }) => {
  const [text, setText] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  // Reflect external selection (e.g. cleared on company switch).
  useEffect(() => {
    setText(pickedLabel || '');
    if (!pickedLabel) setOptions([]);
  }, [pickedLabel]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const onChange = (e) => {
    const value = e.target.value;
    setText(value);
    onPick(null); // typing invalidates the previous selection
    if (timer.current) clearTimeout(timer.current);
    if (!value.trim()) {
      setOptions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await search(value.trim());
        setOptions(results);
        setOpen(true);
      } catch (err) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        value={text}
        onChange={onChange}
        onFocus={() => options.length > 0 && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      {loading && (
        <span className="muted" style={{ position: 'absolute', right: 8, top: 8, fontSize: 12 }}>…</span>
      )}
      {open && options.length > 0 && (
        <ul
          style={{
            position: 'absolute', zIndex: 30, left: 0, right: 0, top: '100%',
            margin: 0, padding: 0, listStyle: 'none', maxHeight: 280, overflowY: 'auto',
            background: 'var(--card-bg, #fff)', border: '1px solid #ccc',
            borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          }}
        >
          {options.map((item, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(item);
                  setText(display(item));
                  setOpen(false);
                }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '7px 10px', border: 'none', background: 'transparent',
                  cursor: 'pointer', font: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {renderOption(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && options.length === 0 && text.trim() && (
        <div className="muted" style={{ position: 'absolute', top: '100%', padding: 6, fontSize: 12 }}>
          No matches.
        </div>
      )}
    </div>
  );
};

const PriceLookup = () => {
  const [company, setCompany] = useState('NZ');
  const [customer, setCustomer] = useState(null);
  const [part, setPart] = useState(null);
  const [qty, setQty] = useState('1');

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);   // informative (dataset miss)
  const [error, setError] = useState(null);     // real errors
  const [result, setResult] = useState(null);
  const [context, setContext] = useState(null);

  // Switching company invalidates both selections and the result.
  const switchCompany = (c) => {
    setCompany(c);
    setCustomer(null);
    setPart(null);
    setResult(null);
    setContext(null);
    setNotice(null);
    setError(null);
  };

  const searchCustomers = useCallback(
    (q) => searchPricingCustomers(company, q).then((d) => d.customers || []),
    [company],
  );
  const searchParts = useCallback(
    (q) => searchPricingParts(company, q).then((d) => d.parts || []),
    [company],
  );

  const lookup = async (e) => {
    e.preventDefault();
    setNotice(null);
    setError(null);
    setResult(null);
    setContext(null);
    if (!customer || !part) {
      setError('Pick a customer and a product from the dropdowns first.');
      return;
    }
    setLoading(true);
    try {
      const [ctx, res] = await Promise.all([
        getCustomerPricingContext(company, customer.cust_num).catch(() => null),
        resolveCustomerPrice(company, customer.cust_num, part.part_num, Number(qty) || 1),
      ]);
      setContext(ctx);
      setResult(res);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 404 && String(detail).includes('pricing dataset')) {
        setNotice(
          `${detail}. The pricing engine hasn't included this record yet — ` +
          'check it is present in staging, or re-run the resolve stage from the Customer Pricing tab.',
        );
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const prov = result?.provenance;
  const qtyNum = Number(qty) || 1;
  // Tier that applies at the requested quantity (tiers are sorted by min_qty).
  const activeTier = (result?.tiers || []).reduce(
    (acc, t) => (t.min_qty <= qtyNum ? t : acc),
    null,
  );
  const priceAtQty = activeTier ? activeTier.unit_price : result?.unit_price_each;

  return (
    <div className="sync-page price-lookup">
      <PageHeader
        title="Price Lookup"
        sub="What does this customer pay for this product? Resolved live from the staged Epicor price lists with full provenance. Read-only — nothing is written anywhere."
        crumbs={[{ label: 'Pricing', to: '/pricing' }, { label: 'Price Lookup' }]}
      />

      <section className="card">
        <form onSubmit={lookup} className="form-grid">
          <div className="form-group">
            <span className="form-label">Company</span>
            <div className="checkbox-row">
              {['AU', 'NZ'].map((c) => (
                <label key={c} className="chip">
                  <input
                    type="radio"
                    name="lookup-company"
                    checked={company === c}
                    onChange={() => switchCompany(c)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group" style={{ minWidth: 320 }}>
            <label className="form-label">Customer (Epicor ID or name)</label>
            <TypeAhead
              placeholder="e.g. ABC100 or ABC Pool"
              search={searchCustomers}
              display={(c) => `${c.name} (${c.cust_id})`}
              pickedLabel={customer ? `${customer.name} (${customer.cust_id})` : ''}
              onPick={setCustomer}
              renderOption={(c) => (
                <span>
                  <strong>{c.name}</strong>{' '}
                  <span className="muted">
                    {c.cust_id} · #{c.cust_num} · {c.group_code || 'no group'}
                    {c.city ? ` · ${c.city}` : ''}
                    {c.has_profile ? '' : ' · ⚠ no pricing profile'}
                  </span>
                </span>
              )}
            />
          </div>

          <div className="form-group" style={{ minWidth: 320 }}>
            <label className="form-label">Product (SKU or name)</label>
            <TypeAhead
              placeholder="e.g. 2204265, MICRON, 2204*"
              search={searchParts}
              display={(p) => `${p.part_num} — ${p.part_description || ''}`}
              pickedLabel={part ? `${part.part_num} — ${part.part_description || ''}` : ''}
              onPick={setPart}
              renderOption={(p) => (
                <span>
                  <strong>{p.part_num}</strong>{' '}
                  <span className="muted">
                    {p.part_description || '(no description)'} · list {fmtMoney(p.unit_price)}
                  </span>
                </span>
              )}
            />
          </div>

          <div className="form-group" style={{ maxWidth: 90 }}>
            <label className="form-label">Qty</label>
            <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>

          <div className="form-group">
            <Button type="submit" disabled={loading || !customer || !part}>
              {loading ? 'Looking up…' : 'Get price'}
            </Button>
          </div>
        </form>

        {error && <p className="error-text">{error}</p>}
        {notice && (
          <p className="muted" style={{ border: '1px solid #e0c068', borderRadius: 6, padding: '8px 10px' }}>
            {notice}
          </p>
        )}
      </section>

      {result && (
        <section className="card">
          <h3 className="section-h">
            {customer?.name} buys {part?.part_num} at
          </h3>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 34, fontWeight: 700 }}>
              {fmtMoney(priceAtQty)}
            </span>
            <span className="muted">
              {context?.customer?.currency_code || (company === 'NZ' ? 'NZD' : 'AUD')} each
              {qtyNum > 1 ? ` at qty ${qtyNum}` : ''}
              {prov?.price_per_code && prov.price_per_code !== 'E'
                ? ` (list maintained per ${prov.price_per_code === 'C' ? '100' : '1000'})`
                : ''}
            </span>
            {part?.unit_price != null && (
              <span className="muted">
                list price {fmtMoney(part.unit_price)}
                {Number(part.unit_price) > 0 && priceAtQty != null
                  ? ` · ${(100 * (1 - priceAtQty / Number(part.unit_price))).toFixed(1)}% off list`
                  : ''}
              </span>
            )}
          </div>

          <table style={{ marginTop: 14 }}>
            <tbody>
              <tr>
                <th style={{ width: 220 }}>Base price</th>
                <td>
                  {fmtMoney(prov?.base_price)}{' '}
                  <span className="muted">
                    {prov?.price_source === 'part_master'
                      ? 'from the Part master'
                      : `from price list ${prov?.price_list_code} (seq ${prov?.price_seq})`}
                  </span>
                </td>
              </tr>
              <tr>
                <th>Discount</th>
                <td>
                  {prov?.discount_percent != null ? `${prov.discount_percent}%` : 'none'}{' '}
                  {prov?.disc_list_code && (
                    <span className="muted">
                      via discount list {prov.disc_list_code} (seq {prov.disc_seq},{' '}
                      {prov.discount_source === 'disc_list_part' ? 'part-specific' : 'product-group'})
                      {prov.discount_applied ? '' : ' — NOT applied'}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th>Customer group / profile</th>
                <td>
                  {context?.customer?.group_code || '—'}{' '}
                  <span className="muted">
                    profile: {context?.customer?.profile_label || 'unresolved'}
                    {context?.customer?.has_shipto_lists ? ' · has ship-to specific lists' : ''}
                  </span>
                </td>
              </tr>
              {result.tiers?.length > 1 && (
                <tr>
                  <th>Quantity breaks</th>
                  <td>
                    {result.tiers.map((t) => (
                      <span
                        key={t.min_qty}
                        className="chip"
                        style={{ marginRight: 8, fontWeight: t === activeTier ? 700 : 400 }}
                      >
                        {t.min_qty}+ → {fmtMoney(t.unit_price)}
                        {t.discount_percent != null ? ` (${t.discount_percent}%)` : ''}
                      </span>
                    ))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <details style={{ marginTop: 12 }}>
            <summary className="muted" style={{ cursor: 'pointer' }}>
              Show the full list stack evaluated for this customer
            </summary>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 10 }}>
              {[['price_lists', 'Unit-price lists'], ['disc_lists', 'Discount lists']].map(
                ([kind, title]) => {
                  const isDisc = kind === 'disc_lists';
                  const cols = isDisc ? 5 : 4;
                  const isWinner = (a) =>
                    a.winner === true ||
                    (isDisc
                      ? a.list_code === prov?.disc_list_code && a.seq === prov?.disc_seq
                      : a.list_code === prov?.price_list_code && a.seq === prov?.price_seq);
                  // Winner first, everyone else in original stack order.
                  const rows = [...(result.assignments?.[kind] || [])]
                    .sort((a, b) => (isWinner(b) ? 1 : 0) - (isWinner(a) ? 1 : 0));
                  return (
                    <table key={kind} style={{ maxWidth: isDisc ? 520 : 460 }}>
                      <thead>
                        <tr><th colSpan={cols}>{title}</th></tr>
                        <tr>
                          <th>Scope</th><th>Seq</th><th>List</th>
                          {isDisc && <th>Discount</th>}
                          <th>Price (each)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!rows.length && (
                          <tr><td colSpan={cols} className="muted">none</td></tr>
                        )}
                        {rows.map((a, i) => {
                          const winning = isWinner(a);
                          return (
                            <tr key={i} style={winning ? { fontWeight: 700 } : undefined}>
                              <td>{a.scope}</td>
                              <td>{a.seq}</td>
                              <td>{a.list_code}{winning ? ' ◀ winner' : ''}</td>
                              {isDisc && (
                                <td>
                                  {a.discount_percent != null ? `${Number(a.discount_percent)}%` : '—'}
                                </td>
                              )}
                              <td>
                                {isDisc
                                  ? a.effective_price != null
                                    ? <>
                                        {fmtMoney(a.effective_price)}
                                        {a.matched && a.discount_applied === false && (
                                          <span className="muted"> (discount not applied)</span>
                                        )}
                                      </>
                                    : '—'
                                  : a.unit_price != null ? fmtMoney(a.unit_price) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                },
              )}
            </div>
          </details>
        </section>
      )}
    </div>
  );
};

export default PriceLookup;
